import dom from '../lib/dom';
import Responsive from '../lib/responsive';
import { isChildNode } from '../lib/helper';
import { trigger } from '../lib/event';

const overlays = {};
let overlayId = 0;
const trackedInputs = window.WeakSet ? new WeakSet() : new Set();

class Overlay {

  constructor(el, isQuickviewOverlay) {

    if (!el) {

      return;
    }

    if (el._id) {

      return overlays[el._id];
    }

    this.setCookieElement = document.querySelector('[data-setcookie]'); 
    this.cookieDomain = (window.app || {}).cookieDomain || window.location.hostname.replace(/^www./, '');

    if (this.setCookieElement) {
      this.setCookieElement.addEventListener('click', this.handleSetCookies.bind(this));
    }

    this.content = el;
    this.showModal = true;
    this.isQuickviewOverlay = isQuickviewOverlay || false;

    // inner content
    this.contentContainer = dom.create('div', {
      class: 'overlay-content'
    }, [this.content]);

    // helper for centering stuff
    this.container = dom.create('div', {
      class: 'overlay-container'
    }, [this.contentContainer]);

    // creates the overlay node
    this.overlay = dom.create('div', {
      class: 'overlay-backdrop'
    }, [this.container]);

    // look for additional triggers
    this.triggers = [].slice.call(this.content.querySelectorAll('.tc-overlay-trigger'));

    this.closeButton = this.content.querySelector('.tc-close-overlay');
    this.closeCheck = this.content.querySelector('.overlay-inner-wrapper') || this.closeButton.parentNode;

    //append to body
    document.body.appendChild(this.overlay);

    overlayId += 1;
    el._id = overlayId;
    overlays[overlayId] = this;

    if (this.isQuickviewOverlay) {

      this.overlay.classList.add('is-quickview-overlay');
    }

    this.registerEvents();

    if (this.content.classList.contains('auto-open-overlay')) {

      this.open();
    }
  }

  handleSetCookies(event) {
    if (event.currentTarget.nodeName == 'A' || event.currentTarget.nodeName == 'BUTTON') {
      const data = JSON.parse(event.currentTarget.getAttribute('data-setcookie'));

      if (!data.value) {
        data.value = 'true';
      }
      if (data.expires) {
        document.cookie = `${data.name}=${data.value};domain=${this.cookieDomain};path=/;max-age=31536000;expires=${data.expires};`;
      } else {
        document.cookie = `${data.name}=${data.value};domain=${this.cookieDomain};path=/;max-age=31536000;`;
      }
    }
  }

  registerEvents() {

    if (this.closeButton) {

      // close it
      this.closeButton.addEventListener('click', this.close.bind(this));
    }

    this.overlay.addEventListener('mousedown', this.onBackdropClick.bind(this));
    this.overlay.addEventListener('click', this.onBackdropClick.bind(this), Modernizr.passiveeventlisteners ? {
      passive: true
    } : false);

    // register close events to additional triggers
    this.triggers.map(trigger => {

      trigger.addEventListener('click', this.close.bind(this));
    });
  }

  open(e) {

    if (e && e.preventDefault) {
      e.preventDefault();
    }

    window.addEventListener('keydown', this.closeOnEsc.bind(this));

    this.overlay.classList.add('is-bound');
    trigger(document, 'tc-overlay-open');

    this.inputs = this.overlay.querySelectorAll('input:not([type="hidden"]), select, textarea, button');

    if (window.IntersectionObserver) {
      this.inputsObserver = new IntersectionObserver((entries) => {
        for (let i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            entries[i].target.focus();
            return;
          }
        }
      }, {
        root: this.overlay,
        rootMargin: '0px',
        threshold: 1.0
      });

      for (let i = 0; i < this.inputs.length; i++) {
        if (!trackedInputs.has(this.inputs[i])) {
          this.inputsObserver.observe(this.inputs[i]);
          trackedInputs.add(this.inputs[i]);
        }
      }
    } else {
      if (this.inputs.length) {
        this.inputs[0].focus();
      }
    }

    if (this.isQuickviewOverlay) {

      if (this.content.id) {

        trigger(document, 'quickview-open' + '#' + this.content.id);
      } else {

        trigger(document, 'quickview-open');
      }
    }
  }

  /**
   * close Lightbox on ESC press
   * @param {event} e 
   */

  closeOnEsc(e) {

    if (!e) {
      return;
    }

    if (e.key === 'Escape') {

      this.close(e);
      e.preventDefault();
    }
  }

  close(e) {

    if (e && e.preventDefault) {

      e.preventDefault();
    }

    this.overlay.classList.remove('is-bound');
    trigger(document, 'tc-overlay-close');

    if (this.isQuickviewOverlay) {

      if (this.content.id) {

        trigger(document, 'quickview-close' + '#' + this.content.id);
      } else {

        trigger(document, 'quickview-close');
      }
    }
  }
  
  onBackdropClick(e) {

    if (
      e.target === this.container ||
      this.closeButton && !isChildNode(e.target, this.closeButton.parentNode) && !isChildNode(e.target, this.closeCheck)
    ) {

      this.close(e);
    }
  }
}


export default class OverlayButton {

  constructor(el) {

    this.el = el;

    this.contentSelecter = this.el.getAttribute('data-content');
    this.content = document.querySelector(this.contentSelecter);
    this.isQuickviewOverlay = false;

    if (this.el.classList.contains('tc-quickview-button')) {

      this.isQuickviewOverlay = true;
    }

    this.overlay = new Overlay(this.content, this.isQuickviewOverlay);

    if (!this.content) {

      return;
    }

    //register additional events if necessary
    this.registerEvents();
  }

  registerEvents() {

    // open it
    this.el.addEventListener('click', this.open.bind(this));
  }

  open(e) {

    e.preventDefault();
    // need to be before e.preventDefault so the trigger has it's default behaviour
    if (!this.overlay.showModal) {

      return;
    }

    const bp = Responsive.getBreakpoint();
    if (this.isQuickviewOverlay && bp === ('s' || 'xs')) {

      return;
    }

    this.overlay.open(e);
  }
}
